import Flickity from 'flickity';

export default () => ({
    videoOpen: false,
    carousel: null,
    carouselTimeout: window.config.CAROUSELS.HERO_VIDEO_CAROUSEL,
    carouselRefreshInterval: null,
    buttons: [],
    loadedVideos: new Set(),
    isTransitioning: false,
    currentVideoPromise: null, // Add this to track current video loading

    init() {
        this.setupVideoPlayback();
        // Preload both first and second videos on init
        Promise.all([
            this.preloadNextVideo(0),
            this.preloadNextVideo(1)
        ]);

        this.carousel = new Flickity(this.$el.querySelector('.hero-video-carousel-instance'), {
            autoPlay: false,
            draggable: true,
            adaptiveHeight: true,
            pageDots: false,
            pauseAutoPlayOnHover: false
        });

        this.buttons = this.$el.querySelectorAll('.button');

        if (this.buttons.length > 0) {
            this.carousel.on('select', async (index) => {
                if (this.isTransitioning) return;
                this.isTransitioning = true;

                // Cancel any ongoing video loading
                if (this.currentVideoPromise) {
                    this.currentVideoPromise = null;
                }

                this.carousel.stopPlayer();
                await this.handleSlideChange(index);
                this.isTransitioning = false;
            });
        }

        this.handleInitialSetup();
    },

    async handleInitialSetup() {
        await this.handleVideoTransition();
        this.fadeInText();
        this.setupLoadingIntro();

        // Start autoplay after initial setup
        this.startAutoPlay();
    },

    async preloadNextVideo(currentIndex) {
        const videos = this.$el.querySelectorAll('video');
        const nextIndex = (currentIndex + 1) % videos.length;
        const nextVideo = videos[nextIndex];

        if (nextVideo && !nextVideo.src && nextVideo.dataset.src) {
            // Store the promise for potential cancellation
            this.currentVideoPromise = new Promise((resolve) => {
                nextVideo.src = nextVideo.dataset.src;
                nextVideo.preload = 'auto';

                const handleLoad = () => {
                    // Only proceed if this is still the current loading promise
                    if (this.currentVideoPromise) {
                        nextVideo.removeEventListener('loadeddata', handleLoad);
                        this.loadedVideos.add(nextVideo);
                        resolve();
                    }
                };

                nextVideo.addEventListener('loadeddata', handleLoad);

                // Increased timeout for slower connections
                setTimeout(() => {
                    if (this.currentVideoPromise) {
                        resolve();
                    }
                }, 8000);
            });

            return this.currentVideoPromise;
        }
        return Promise.resolve();
    },

    setupVideoPlayback() {
        this.$el.querySelectorAll('video').forEach((video, index) => {
            video.setAttribute('playsinline', 'true');
            video.setAttribute('webkit-playsinline', 'true');
            video.muted = true;

            // Enhanced error handling
            video.addEventListener('error', async (e) => {
                console.error('Video error:', e);
                if (video.src && !this.loadedVideos.has(video)) {
                    await this.retryVideoLoad(video);
                }
            });
        });
    },

    async retryVideoLoad(video, maxAttempts = 3) {
        let attempts = 0;
        while (attempts < maxAttempts) {
            try {
                const currentSrc = video.src || video.dataset.src;
                video.src = '';
                await new Promise(resolve => setTimeout(resolve, 1000));
                video.src = currentSrc;

                await new Promise((resolve, reject) => {
                    video.addEventListener('loadeddata', resolve, { once: true });
                    video.addEventListener('error', reject, { once: true });
                });

                this.loadedVideos.add(video);
                return true;
            } catch (error) {
                attempts++;
                console.error(`Video load attempt ${attempts} failed:`, error);
            }
        }
        return false;
    },

    async handleVideoTransition() {
        const activeVideo = this.$el.querySelector('.carousel-cell.is-selected video');
        if (!activeVideo) return;

        // Clear any existing timeouts
        if (this.carouselRefreshInterval) {
            clearInterval(this.carouselRefreshInterval);
        }

        // Ensure all other videos are properly paused
        this.$el.querySelectorAll('video').forEach(video => {
            if (video !== activeVideo && !video.closest('#plyr-wrapper')) {
                video.pause();
                video.currentTime = 0;
            }
        });

        try {
            if (!activeVideo.src && activeVideo.dataset.src) {
                activeVideo.src = activeVideo.dataset.src;
            }

            activeVideo.currentTime = 0;
            activeVideo.muted = true;

            // Wait for video to be ready
            if (activeVideo.readyState < 3) {
                await new Promise((resolve) => {
                    const handleReady = () => {
                        activeVideo.removeEventListener('canplay', handleReady);
                        activeVideo.removeEventListener('loadeddata', handleReady);
                        resolve();
                    };

                    activeVideo.addEventListener('canplay', handleReady);
                    activeVideo.addEventListener('loadeddata', handleReady);

                    setTimeout(resolve, 8000); // Increased timeout
                });
            }

            await this.playVideoWithRetry(activeVideo);

        } catch (error) {
            console.error('Video transition error:', error);
        }
    },

    async playVideoWithRetry(video, maxAttempts = 3) {
        let attempts = 0;
        while (attempts < maxAttempts) {
            try {
                const playPromise = video.play();
                if (playPromise !== undefined) {
                    await playPromise;
                    return true;
                }
            } catch (error) {
                attempts++;
                console.log(`Play attempt ${attempts} failed:`, error);

                if (attempts === maxAttempts) {
                    video.muted = true;
                    await video.play().catch(e => console.error('Final play attempt failed:', e));
                }

                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }
        return false;
    },

    async handleSlideChange(index) {
        // Update button states
        this.buttons.forEach((button, btnIndex) => {
            if (btnIndex === index) {
                button.classList.add('active');
                button.querySelector('circle').style.strokeDashoffset = '300px';
            } else {
                button.classList.remove('active');
            }
        });

        // Handle video transition
        await this.handleVideoTransition();

        // Start preloading next video
        await this.preloadNextVideo(index);

        // Update UI
        this.handleCarouselTimerButton();
        this.fadeInText();

        // Update duration
        this.setSlideDuration(index);
    },

    startAutoPlay() {
        const duration = this.getInitialAutoPlayDuration();
        setTimeout(() => {
            if (!this.isTransitioning) {
                this.carousel.next(true);
            }
        }, duration);
    },

    handleCarouselTimerButton() {
        if (this.carouselRefreshInterval) {
            clearInterval(this.carouselRefreshInterval);
        }

        let offset = 300;
        const intervalDuration = this.carouselTimeout / offset;

        this.carouselRefreshInterval = setInterval(() => {
            offset--;

            const currentButton = this.buttons[this.carousel.selectedIndex];
            if (!currentButton) return;

            const diamondWhiteBorder = currentButton.querySelector('.diamond-active-border');
            if (diamondWhiteBorder) {
                diamondWhiteBorder.style.strokeDashoffset = `${offset}px`;
            }

            if (offset <= 0) {
                clearInterval(this.carouselRefreshInterval);
                this.carouselRefreshInterval = null;
                if (!this.isTransitioning) {
                    this.carousel.next(true);
                }
            }
        }, intervalDuration);
    },

    fadeInText() {
        this.$el.querySelectorAll('.carousel-meta').forEach((textSlide, index) => {
            const innerElements = textSlide.querySelectorAll('span');

            if (index === this.carousel.selectedIndex) {
                innerElements.forEach(element => element.classList.add('fade-up-in'));
            } else {
                innerElements.forEach(element => element.classList.remove('fade-up-in'));
            }
        });
    },

    setSlideDuration(index) {
        const videos = this.$el.querySelectorAll('video');
        if (!videos?.length) return;

        const selectedVideo = videos[index];
        if (!selectedVideo) return;

        // Clear any existing timeouts
        if (this.autoPlayTimeout) {
            clearTimeout(this.autoPlayTimeout);
        }

        const videoDuration = selectedVideo.dataset?.videoDuration
            ? Number(selectedVideo.dataset.videoDuration)
            : window.config.CAROUSELS.HERO_VIDEO_CAROUSEL;

        this.carouselTimeout = videoDuration;

        // Store timeout reference
        this.autoPlayTimeout = setTimeout(() => {
            if (!this.isTransitioning) {
                this.carousel.next(true);
            }
        }, videoDuration);
    },

    handleClick(index) {
        if (!this.isTransitioning) {
            this.carousel.select(index);
            this.carousel.reposition();
        }
    },

    setupLoadingIntro() {
        const body = document.body;
        if (!body.dataset.loading || body.dataset.loading !== 'true') {
            this.setSlideDuration(this.carousel.selectedIndex);
            this.handleCarouselTimerButton();
            return;
        }

        const videos = this.$el.querySelectorAll('.hero-video-carousel video');
        videos.forEach(video => {
            video.currentTime = 0;
            video.pause();
        });

        if (this.carousel) {
            this.carousel.pausePlayer();
        }

        const introScene = document.getElementById('intro-video');
        if (introScene) {
            introScene.addEventListener('ended', this.handleIntroEnd.bind(this));
        }
    },

    async handleIntroEnd() {
        const loadingScene = document.getElementById('loading-scene');
        if (loadingScene) {
            loadingScene.classList.add('animate-loading');
        }

        await this.handleVideoTransition();

        if (this.carousel) {
            this.setSlideDuration(this.carousel.selectedIndex);
            this.handleCarouselTimerButton();
        }

        const carouselMeta = this.$el.querySelectorAll('.carousel-meta');
        carouselMeta.forEach(meta => {
            meta.classList.remove('hidden');
            meta.classList.add('fade-up-in');
        });

        const headerElement = document.querySelector('header nav');
        if (headerElement) {
            headerElement.classList.add('visible');
        }

        setTimeout(() => {
            document.body.setAttribute('data-loading', 'false');
        }, 250);
    },

    getInitialAutoPlayDuration() {
        const firstSlideVideo = this.$el.querySelector('.carousel-cell:first-child video');
        if (firstSlideVideo?.dataset.videoDuration) {
            return parseInt(firstSlideVideo.dataset.videoDuration, 10);
        }
        return window.config.CAROUSELS.HERO_VIDEO_CAROUSEL;
    }
});